import UploadProgressBar from "@/common/components/UploadProgressBar";
import { RecordingWidgetState, useAssetLibraryProvider } from "@/state/providers/AssetLibraryProvider";
import ArrowRightIcon from "@icons/arrow-right.svg?react";
import classNames from "classnames";
import { useNavigate, useLocation } from "react-router";

interface Props {
    uploadProgress: number;
    newPatientRecordId: string | null;
    setNewPatientRecordId: (patientRecordId: string | null) => void;
}

// view for when widget is in the "uploading" and "saved" states (after recording)
const UploadView: React.FC<Props> = ({ uploadProgress, newPatientRecordId, setNewPatientRecordId }) => {
    // Don't show the visit button on the appointments page
    // because recordings could end up going to a new patient
    const showVisitButton = !/^\/appointments\//.test(useLocation().pathname);

    /**
     * Audio Recording Provider
     * hook for managing widget state, audio recording, file upload, document select, patient data
     **/
    const {
        // recording widget controls
        recordingWidgetControls: { recordingWidgetState, setRecordingWidgetState, setRenderRecordingWidget },
        // file upload controls
        recordingFileUploadControls: { uploadMessage },
    } = useAssetLibraryProvider();

    const navigate = useNavigate();

    // disable the action buttons if the widget is not in the saved state
    const buttonsDisabled = recordingWidgetState !== RecordingWidgetState.SAVED;

    /**
     * Close the recording widget
     * handler for the close button in the upload view
     */
    const handleClose = () => {
        setRecordingWidgetState(RecordingWidgetState.WAITING);
        setNewPatientRecordId(null);

        // close the widget if not on the home page
        if (location.pathname !== "/") {
            setRenderRecordingWidget(false);
        }
    };

    /**
     * Visit the patient
     * handler for the visit button in the upload view
     */
    const handleVisit = () => {
        setRecordingWidgetState(RecordingWidgetState.WAITING);
        if (newPatientRecordId) {
            navigate(`/appointments/${newPatientRecordId}`);
            setNewPatientRecordId(null);
        }
    };

    return (
        <div className="w-full h-full flex flex-col">
            {/* Progress and text container */}
            <div className="px-6 pt-6 pb-2 flex flex-col justify-start flex-grow">
                <div className="h-2 w-3/4">
                    <UploadProgressBar percentage={uploadProgress} displayPercentage={false} />
                </div>
                <div className="text-left text-white text-sm font-medium leading-tight mt-4" aria-live="polite">
                    {recordingWidgetState === RecordingWidgetState.UPLOADING &&
                        "Saving your clip – this usually takes several seconds. It will appear in your library automatically."}
                    {recordingWidgetState === RecordingWidgetState.SAVED &&
                        "Recorded clip has been saved. It will appear in your library automatically."}
                    {recordingWidgetState === RecordingWidgetState.UPLOADING_ERROR &&
                        "Failed to save your recorded clip."}
                </div>
            </div>

            {/* warning and error messages */}
            {uploadMessage && (
                <div
                    className={classNames(
                        "flex items-center justify-center py-2 px-2 text-xs text-start font-medium",
                        uploadMessage.type === "error" ? "bg-[#F04438] text-[#FEE4E2]" : "bg-[#F79009] text-[#FEF0C7]",
                    )}
                >
                    <span>{uploadMessage.message}</span>
                </div>
            )}

            {/* buttons section */}
            <div className="h-[38px] px-6 py-2 mb-4 flex flex-row justify-between">
                <button
                    className={classNames(
                        "h-[27px] px-2 bg-white rounded-md shadow border border-shadow-500 justify-center items-center gap-1 inline-flex",
                        buttonsDisabled ? "border-shadow-300 text-shadow-300" : "border-shadow-500 text-shadow-500",
                    )}
                    onClick={handleClose}
                    disabled={buttonsDisabled}
                >
                    <div className="text-xs font-semibold leading-[18px]">Close</div>
                </button>
                {showVisitButton && (
                    <button
                        data-tour="recording-widget-visit"
                        className={classNames(
                            "h-[26px] px-2 bg-white rounded-md shadow border justify-center items-center gap-1 inline-flex",
                            buttonsDisabled ? "border-shadow-300 text-shadow-300" : "border-max-800 text-max-800",
                        )}
                        onClick={handleVisit}
                        disabled={buttonsDisabled}
                    >
                        <div className="text-xs font-semibold leading-[18px]">Visit</div>
                        <ArrowRightIcon className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default UploadView;
